/**
 * FacultySchedulePage - Manage schedule and availability
 */

import { useState, useEffect } from 'react';
import FacultyLayout from './FacultyLayout';
import {
  getFacultyDashboard,
  getAvailability,
  addAvailabilityBlock,
  removeAvailabilityBlock,
  type FacultyUpcomingClass,
  type AvailabilityBlock,
} from '../../api/faculty';
import { formatTimeDisplay } from '@sda/shared';
import { parseLocalDate } from '../../utils/date';

export default function FacultySchedulePage() {
  const [upcomingClasses, setUpcomingClasses] = useState<FacultyUpcomingClass[]>([]);
  const [availabilityBlocks, setAvailabilityBlocks] = useState<AvailabilityBlock[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddBlockModal, setShowAddBlockModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [dashboardData, availability] = await Promise.all([
        getFacultyDashboard(),
        getAvailability(),
      ]);
      setUpcomingClasses(dashboardData.upcoming_classes);
      setAvailabilityBlocks(availability);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load schedule');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveBlock = async (blockId: string) => {
    if (!confirm('Remove this unavailability block?')) return;

    try {
      await removeAvailabilityBlock(blockId);
      setAvailabilityBlocks((prev) => prev.filter((b) => b.id !== blockId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove block');
    }
  };

  if (isLoading) {
    return (
      <FacultyLayout>
        <div className="max-w-4xl mx-auto">
          <div className="h-8 bg-warm-100 rounded w-1/3 animate-pulse mb-8" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="card animate-pulse">
                <div className="h-4 bg-warm-100 rounded w-3/4 mb-2" />
                <div className="h-4 bg-warm-100 rounded w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </FacultyLayout>
    );
  }

  if (error) {
    return (
      <FacultyLayout>
        <div className="max-w-4xl mx-auto">
          <div className="card text-center py-12">
            <p className="text-danger-600 mb-4">{error}</p>
            <button onClick={loadData} className="btn-primary">
              Try Again
            </button>
          </div>
        </div>
      </FacultyLayout>
    );
  }

  return (
    <FacultyLayout>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900 mb-2">
              My Schedule
            </h1>
            <p className="text-warm-600">
              View your upcoming classes and manage your availability.
            </p>
          </div>
        </div>

        {/* Upcoming Classes */}
        <section className="mb-10">
          <h2 className="text-xl font-display font-bold text-warm-900 mb-4">
            Upcoming Classes
          </h2>

          {upcomingClasses.length === 0 ? (
            <div className="card text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warm-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-warm-900 mb-2">No Upcoming Classes</h3>
              <p className="text-warm-600">
                You'll see your scheduled classes here once you're assigned.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {upcomingClasses.map((classItem) => (
                <div key={classItem.id} className="card">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    {/* Show Logo */}
                    <div className="w-14 h-14 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      {classItem.show_logo_url ? (
                        <img
                          src={classItem.show_logo_url}
                          alt={classItem.show_name}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <span className="text-primary-500 text-xl font-display font-bold">
                          {classItem.show_name.charAt(0)}
                        </span>
                      )}
                    </div>

                    {/* Class Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-warm-900">{classItem.show_name}</h3>
                      <p className="text-sm text-warm-600">
                        {new Date(classItem.scheduled_date + 'T12:00:00').toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                        })} • {formatTimeDisplay(classItem.start_time)}
                      </p>
                      <p className="text-sm text-warm-500">
                        {classItem.studio_name} • {classItem.studio_city}, {classItem.studio_state}
                      </p>
                    </div>

                    {/* Status & Details */}
                    <div className="text-right">
                      <span className="px-3 py-1 rounded-full text-sm font-medium bg-success-100 text-success-700">
                        Confirmed
                      </span>
                      {classItem.student_count && (
                        <p className="text-sm text-warm-500 mt-1">
                          {classItem.student_count} students
                        </p>
                      )}
                      {classItem.student_level && (
                        <p className="text-xs text-warm-400 mt-0.5">
                          {classItem.student_level}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Special Requests */}
                  {classItem.special_requests && (
                    <div className="mt-4 pt-4 border-t border-warm-200">
                      <p className="text-sm text-warm-500 mb-1">Special Requests:</p>
                      <p className="text-sm text-warm-700">{classItem.special_requests}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Availability Management */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-display font-bold text-warm-900">
              Unavailability
            </h2>
            <button
              onClick={() => setShowAddBlockModal(true)}
              className="btn-primary"
            >
              <span className="hidden sm:inline">Add Unavailable </span>Dates
            </button>
          </div>

          <p className="text-warm-600 text-sm mb-4">
            Mark dates when you are not available for class assignments.
          </p>

          {availabilityBlocks.length === 0 ? (
            <div className="card text-center py-8">
              <p className="text-warm-600">
                No unavailable dates set. You're available for all assignments.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {availabilityBlocks.map((block) => (
                <div key={block.id} className="card flex items-center justify-between">
                  <div>
                    <p className="font-medium text-warm-900">
                      {block.block_type === 'date' ? (
                        parseLocalDate(block.start_date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      ) : (
                        <>
                          {parseLocalDate(block.start_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                          })}
                          {' — '}
                          {block.end_date && parseLocalDate(block.end_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </>
                      )}
                    </p>
                    {block.reason && (
                      <p className="text-sm text-warm-500 mt-1">{block.reason}</p>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemoveBlock(block.id)}
                    className="p-2 text-warm-400 hover:text-danger-600 transition-colors"
                    title="Remove"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Add Block Modal */}
        {showAddBlockModal && (
          <AddBlockModal
            onClose={() => setShowAddBlockModal(false)}
            onAdd={async (data) => {
              const newBlock = await addAvailabilityBlock(data);
              setAvailabilityBlocks((prev) => [...prev, newBlock]);
              setShowAddBlockModal(false);
            }}
          />
        )}
      </div>
    </FacultyLayout>
  );
}

/**
 * AddBlockModal - Modal for adding unavailability blocks
 */
interface AddBlockModalProps {
  onClose: () => void;
  onAdd: (data: { block_type: 'date' | 'range'; start_date: string; end_date?: string; reason?: string }) => Promise<void>;
}

function AddBlockModal({ onClose, onAdd }: AddBlockModalProps) {
  const [blockType, setBlockType] = useState<'date' | 'range'>('date');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!startDate) {
      setError('Please select a date');
      return;
    }

    if (blockType === 'range' && !endDate) {
      setError('Please select an end date');
      return;
    }

    if (blockType === 'range' && new Date(endDate) <= new Date(startDate)) {
      setError('End date must be after start date');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      await onAdd({
        block_type: blockType,
        start_date: startDate,
        end_date: blockType === 'range' ? endDate : undefined,
        reason: reason || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add unavailability');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-display font-bold text-warm-900 mb-4">
          Add Unavailable Dates
        </h2>

        <form onSubmit={handleSubmit}>
          {/* Block Type */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-warm-700 mb-2">
              Type
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="blockType"
                  value="date"
                  checked={blockType === 'date'}
                  onChange={() => setBlockType('date')}
                  className="mr-2"
                />
                <span className="text-sm text-warm-700">Single Date</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="blockType"
                  value="range"
                  checked={blockType === 'range'}
                  onChange={() => setBlockType('range')}
                  className="mr-2"
                />
                <span className="text-sm text-warm-700">Date Range</span>
              </label>
            </div>
          </div>

          {/* Start Date */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-warm-700 mb-1">
              {blockType === 'date' ? 'Date' : 'Start Date'}
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* End Date (for ranges) */}
          {blockType === 'range' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-warm-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          )}

          {/* Reason */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-warm-700 mb-1">
              Reason (optional)
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Vacation, Personal commitment"
              className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary"
            >
              {isSubmitting ? 'Adding...' : 'Add Unavailability'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
